import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import ServiceEntity, { ServiceStatus } from '../../../domain/entities/ServiceEntity';
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent';
import NotFoundComponent from '../../components/notFound/NotFoundComponent';
import { isRight, right } from 'fp-ts/lib/Either';
import ModalsContext from '../../providers/modal/ModalsContext';
import ModalsContextType from '../../providers/modal/modals/ModalsContextType';
import LayoutComponent from '../../components/layouts/layoutComponent/LayoutComponent';
import ParkedStatusCard from './components/ParkedStatusCard';
import DeliveryStartedStatusCard from './components/DeliveryStartedStatusCard';
import OnWayToDeliverStatusCard from './components/OnWayToDeliverStatusCard';
import WaitingForPickupStatusCard from './components/WaitingForPickupStatusCard';
import DeliveredStatusCard from './components/DeliveredStatusCard';
import StepperComponent from './components/stepper/StepperComponent';
import TermsAndConditionsComponent from './components/TermsAndConditionsComponent';
import ServiceRepository from '../../../domain/repositories/ServiceRepository';
import ServiceRepositoryImpl from '../../../data/repositories/service/ServiceRepositoryImpl';

const QRCodeCheckPage: React.FC = () => {
    const { qrcode } = useParams<{ qrcode: string }>();
    const [service, setService] = useState<null | undefined | ServiceEntity>(undefined);
    const [loading, setLoading] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const { addToast } = useContext(ModalsContext) as ModalsContextType;
    const serviceRepository: ServiceRepository = new ServiceRepositoryImpl();

    const _handleLoadService = async ({ polling = false }: { polling?: boolean } = {}) => {
        if (!qrcode) {
            setService(null);
            return;
        }

        if (!polling) setService(undefined);
        const result = await serviceRepository.getServiceById(qrcode);

        if (isRight(result)) {
            setService(result.right);
            _updatePollingStatus(result.right.status);
        } else {
            _updatePollingStatus(undefined);
            setService(null);
            addToast(result.left ?? "Error al cargar el servicio", 'error', {});
        }
    };

    const _updatePollingStatus = async (serviceStatus?: ServiceStatus) => {
        setIsPolling(serviceStatus != null && serviceStatus !== ServiceStatus.Delivered && serviceStatus !== ServiceStatus.Parked);
    }

    const _handleAcceptTerms = async () => {
        if (!service) return;

        setLoading(true);
        try {
            const result = await serviceRepository.acceptTermsAndConditions(service.id);

            if (isRight(result)) {
                _handleLoadService();
                addToast('Términos y condiciones aceptados', 'success', {});
            } else {
                addToast(result.left, 'error', {});
            }
        } catch (error) {
            addToast('Error al aceptar términos y condiciones', 'error', {});
        }
        setLoading(false);
    };

    const _handleRequestPickup = async () => {
        if (!service) return;

        try {
            const result = await serviceRepository.requestCarPickup(service.id);

            if (isRight(result)) {
                addToast('Solicitud de entrega enviada exitosamente', 'success', {});
                _handleLoadService({ polling: true });
                _updatePollingStatus(ServiceStatus.DeliveriedStarted);
            } else {
                addToast("Error al solicitar entrega del vehículo", 'error', {});
            }
        } catch (error) {
            addToast('Error al solicitar entrega del vehículo', 'error', {});
        }
    };


    // Effect para cargar el servicio inicial
    useEffect(() => {
        _handleLoadService();
        if (isPolling) {
            // Si está haciendo polling cuando cambia el QR, detenerlo
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            setIsPolling(false);
        }
    }, [qrcode]);

    // Effect para polling del estado del servicio
    useEffect(() => {
        if (!isPolling) {
            // Limpiar intervalo si existe
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            return;
        }

        pollingIntervalRef.current = setInterval(() => {
            console.log('Polling para actualizar estado del servicio...');
            _handleLoadService({ polling: true });
        }, 20000); // Cada 5 segundos

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [isPolling]);

    return (
        <div className="qr_code_check_page">
            {service === undefined && <div className="center">
                <LoadingComponent showLogo fullScreen />
            </div>}
            {service === null && <div className="center">
                <NotFoundComponent />
            </div>}
            {
                service && !service.acceptedTyC &&
                <TermsAndConditionsComponent
                    qrId={service.qrId}
                    shopName={service.shop.name}
                    carBrand={service.car.brand}
                    carModel={service.car.model}
                    carColor={service.car.color}
                    carPlate={service.car.plate}
                    onAccept={_handleAcceptTerms}
                    loading={loading}
                />
            }
            {service && service.acceptedTyC == true &&
                <LayoutComponent>
                    <div className="container mt-md-5">

                        {(service.status === ServiceStatus.Parked || service.status === ServiceStatus.WaitingForAcceptance) && (
                            <ParkedStatusCard
                                status={service.status}
                                carPlate={service.car.plate}
                                onRequestPickup={_handleRequestPickup}
                            />
                        )}

                        {service.status === ServiceStatus.DeliveriedStarted && (
                            <DeliveryStartedStatusCard carPlate={service.car.plate} />
                        )}

                        {service.status === ServiceStatus.OnWayToDeliver && (
                            <OnWayToDeliverStatusCard carPlate={service.car.plate} />
                        )}

                        {service.status === ServiceStatus.WaitingForPickup && (
                            <WaitingForPickupStatusCard carPlate={service.car.plate} />
                        )}

                        {service.status === ServiceStatus.Delivered && (
                            <DeliveredStatusCard carPlate={service.car.plate} />
                        )}
                        {isPolling && <StepperComponent currentStatus={service.status} />}

                    </div>
                </LayoutComponent>
            }
        </div>
    );
};

export default QRCodeCheckPage;
